import { isAuthenticatedWeeklyReportOwner } from '../../../lib/weeklyReportOwnerAccess';

export type WeeklyReportVisibilityDecision = {
  allowed: boolean;
  /**
   * true only when every report-type token on the row resolved to an explicit
   * publish-scope setting. Non-owner recovery rows fail closed when false.
   */
  configured: boolean;
};

type WeeklyReportRowLike = {
  wr_id?: unknown;
  create_by_user_id?: unknown;
  createByUserId?: unknown;
};

/**
 * Build the candidate-recovery request used when the viewer-scoped API omits
 * a row published to another department/team or authored by the viewer.
 *
 * Both viewer fields must be removed. Leaving USER_TEAM_ID behind would still
 * let the API reject a department-published row before the client can apply
 * the canonical option_id based access decision.
 */
export const buildWeeklyReportVisibilityRecoveryParams = (
  params: URLSearchParams
): URLSearchParams => {
  const recoveryParams = new URLSearchParams(params);
  recoveryParams.delete('USER_DEP');
  recoveryParams.delete('USER_TEAM_ID');
  return recoveryParams;
};

/**
 * Merge the normal viewer-scoped response with candidate recovery rows.
 * The authenticated author can always view their own report, even when the
 * author is outside its explicit publish scope. Everyone else must satisfy the
 * configured scope. Both responses must already satisfy the same search
 * conditions.
 */
export const mergeWeeklyReportRowsWithVisibilityRecovery = <
  T extends WeeklyReportRowLike,
>(args: {
  scopedRows: T[];
  recoveryRows: T[];
  authenticatedUserId?: unknown;
  decideVisibility: (row: T) => WeeklyReportVisibilityDecision;
}): T[] => {
  const seen = new Set<string>();
  const merged: T[] = [];

  const append = (row: T) => {
    const rawWrId = row?.wr_id;
    const wrId = rawWrId == null ? '' : String(rawWrId).trim();
    if (wrId && seen.has(wrId)) return;
    if (wrId) seen.add(wrId);
    merged.push(row);
  };

  (args.scopedRows ?? []).forEach((row) => {
    const decision = args.decideVisibility(row);
    const isOwner = isAuthenticatedWeeklyReportOwner(row, args.authenticatedUserId);
    if (isOwner || decision.allowed) append(row);
  });

  (args.recoveryRows ?? []).forEach((row) => {
    const decision = args.decideVisibility(row);
    const isOwner = isAuthenticatedWeeklyReportOwner(row, args.authenticatedUserId);
    if (isOwner) {
      append(row);
      return;
    }
    if (decision.configured && decision.allowed) append(row);
  });

  return merged;
};
